<template>
  <section v-if="orderedColumns.length > 0">
    <div
      class="table-container"
      :class="{ 'no-shadow': !castShadow, 'no-table': !showTable, 'no-padding': noPadding }"
      @mouseup.stop.prevent="handleTableMouseUp"
      @mousemove.stop.prevent="handleTableMouseMove">
      <slot name="before-filters" />

      <div
        v-show="showFilters"
        class="filter-row">
        <TableFilters
          ref="tableFilters"
          :columns="columns"
          :table-settings="tableSettings"
          :enable-search-section="enableSearchSection"
          :enable-views="enableViews"
          :category="category"
          :show-headers="showHeaders"
          :show-filters="showFilters"
          :show-save-btn="showSaveBtn"
          :use-filters-drop-down="useFiltersDropDown"
          :allow-comparison="allowComparison"
          :is-main-table="isMainTable"
          :fixed-filters="fixedFilters"
          :text-search="textSearch"
          :text-search-properties="textSearchProperties"
          :enable-only-search-section="enableOnlySearchSection"
          @filters-changed="() => fireFiltersChanged()"
          @toggle-comparison="(value) => emits('toggleComparison', value)"
          @text-search-term-changed="(value) => emits('textSearchTermChanged', value)">
          <template #actions>
            <slot name="actions" />
            <template v-if="showTable">
              <button-group
                v-if="buttonActionOptions"
                :title="buttonActionOptions.label"
                :tooltip="buttonActionOptions.tooltip">
                <button
                  v-for="btnOption in buttonActionOptions.options"
                  :key="btnOption.id"
                  class="btn btn-xs btn-default"
                  @click="() => emits('onButtonActionOptionsChanged', btnOption.id)">
                  {{ btnOption.label }}
                </button>
              </button-group>
              <div
                v-if="enableColumnReorder && showHeaders"
                class="change-columns-container">
                <button
                  v-tooltip.left="capitalize($t('messages.changeColumnOrderAndVisibility'))"
                  class="btn btn-default btn-sm btn-no-text change-columns"
                  @click="showColumnsModal">
                  <IconColumns />
                </button>
              </div>
              <button
                v-show="enableDownload"
                v-tooltip.top="capitalize($t('actions.download'))"
                class="btn btn-default btn-sm btn-no-text download-list-as-csv"
                @click="() => tableDownloadModal.open()">
                <IconDownload />
              </button>
            </template>
          </template>
          <template #extra-toolbar>
            <slot name="extra-toolbar" />
          </template>
          <template #extra-controls>
            <slot name="extra-controls" />
          </template>
        </TableFilters>
      </div>
      <div
        v-if="isSelectAllItemsInSearchLinkVisible && getAction != null"
        class="well dark text-center mb-3 flex-h-centered">
        <template v-if="!didChooseToSelectAllItemsInSearch">
          <span
            v-sanitize-html="
              $t('messages.allXItemsOnThisPareAreSelected', [formatNumberAndBoldHtml(itemsInternal.length)])
            " />
        </template>
        <template v-else-if="totalCount > maximumBulkUpdateRecordCount">
          <span
            v-sanitize-html="
              $t('messages.xOutOfYItemsOnThisPageAreSelected', [
                formatNumberAndBoldHtml(maximumBulkUpdateRecordCount),
                formatNumberAndBoldHtml(totalCount),
              ])
            " />
        </template>
        <template v-else>
          <span
            v-sanitize-html="
              $t('messages.allXItemsMatchingThisSearchAreSelected', [formatNumberAndBoldHtml(totalCount)])
            " />
        </template>
        &nbsp;
        <a
          v-if="!didChooseToSelectAllItemsInSearch"
          @click="() => selectAllItemsInSearch()">
          <b>{{ $t('messages.selectAllItemsThatMatchThisSearch') }}</b>
        </a>
        <span
          v-else-if="isLoadingSelections"
          class="flex-h text-muted">
          <LoaderSmall class="ml-5 mr-3" />
          <i>{{ $t('messages.loadingSelection') }}</i>
        </span>
        <a
          v-else
          @click="() => clearSelection()">
          <b>{{ $t('messages.clearSelection') }}</b>
        </a>
      </div>
      <div
        v-show="showTable && !noResults"
        ref="tableScroller"
        class="table-scroller"
        :class="tableScrollerClassNames">
        <table
          class="entity-table table"
          :class="{
            editable: isEditable,
            'draw-columns': shouldDrawColumns,
            'expandable-table': allowRowExpansion,
            'is-loading': isLoading,
          }">
          <thead v-show="showHeaders">
            <tr ref="headers">
              <th
                v-if="allowSelection"
                class="selection-header">
                <div
                  v-if="allowSelectionAll"
                  class="cell-wrapper">
                  <div class="checkbox">
                    <input
                      :disabled="!allowSelectionAll"
                      class="toggle-all"
                      type="checkbox"
                      :checked="allSelected"
                      @change="toggleAll" />
                  </div>
                </div>
              </th>
              <th
                v-if="allowRowExpansion"
                class="expansion-header">
                <ExpandButton
                  :model-value="!isLoading && isAllExpanded"
                  @update:model-value="toggleExpandAll" />
              </th>
              <template
                v-for="(column, i) in orderedColumns"
                :key="`col-${column.label}`">
                <th
                  class="data-header"
                  :class="`${i === 0 ? 'first-column' : ''} ${canSort(column) ? `enabled col-${column.property.replace(/\./g, '-')}` : ''}`"
                  @click="(event) => sortBy(column, event)">
                  <div
                    v-tooltip.top="column.description"
                    class="cell-wrapper"
                    :class="{ right: column.right }"
                    :style="getColumnHeaderCellWrapperStyle(column, i)">
                    <span
                      data-entity-type="column-title"
                      :class="{ 'no-text-transform': !column.shouldBeUppercased }">
                      {{ column.label }}
                    </span>
                    <span
                      v-if="
                        column.allowSort &&
                        sortColumnInternal != null &&
                        sortColumnInternal.property === column.property
                      "
                      class="sort">
                      <IconSortAsc v-if="sortAscendingInternal" />
                      <IconSortDesc v-if="!sortAscendingInternal" />
                    </span>
                    <span
                      class="column-sizer"
                      @mousedown="(e) => handleColumnSizerMouseDown(e, column)">
                      <span class="line"></span>
                    </span>
                  </div>
                </th>
              </template>
            </tr>
          </thead>
          <tbody>
            <template v-if="itemsInternal.length > 0 && !isLoading">
              <template
                v-for="(item, lineNumber) in itemsInternal"
                :key="`row-${lineNumber}`">
                <tr
                  :class="`${getItemType(item) ?? ''} data-row ${getAlternatingStyle(lineNumber)}`"
                  @click="onRowClick(item, lineNumber, $event)">
                  <!-- SELECTION CHECKBOX -->
                  <td
                    v-if="allowSelection"
                    class="selection-cell">
                    <Selection
                      v-tooltip="selectionTooltipMessage ? selectionTooltipMessage(item) : null"
                      :item="item"
                      :selected-items="selectedItems"
                      :disabled="lockSelection || !validateCanSelectItem(item, selectedItems)"
                      :reset-on-unmount="resetSelectionOnUpdate"
                      :track-by="trackBy"
                      @selection-changed="(selected) => handleItemSelection(item, selected)" />
                  </td>

                  <td
                    v-if="allowRowExpansion"
                    class="expansion-cell">
                    <ExpandButton :model-value="expandedRows.has(lineNumber)" />
                  </td>

                  <!-- COLUMNS TEMPLATES -->
                  <template
                    v-for="(column, i) in orderedColumns"
                    :key="`row-${lineNumber}-col-${column.key}`">
                    <td
                      class="data-cell"
                      :class="{ 'first-column': i === 0 }"
                      :style="dataRowStyle(item, column)"
                      @mouseenter="(e) => onDataRowCellMouseEnter(e, item, column)"
                      @mouseleave="() => onDataRowCellMouseLeave()">
                      <div
                        :style="rowFullHeight ? 'height: 100%' : ''"
                        class="d-flex">
                        <div
                          class="cell-wrapper"
                          :data-key="`cell-wrapper-${column.key}`"
                          :class="{ money: column.type === TableColumnTypes.currency, right: column.right }"
                          :style="`${column.style};
                            ${i === 0 ? 'padding-top: 0px; padding-bottom: 0px;' : ''};`"
                          @dblclick="(event) => onCellDoubleClick(event, item, column)">
                          <TableCell
                            v-if="column.fragments === undefined"
                            :item="item"
                            :column="column"
                            :highlighted-columns="highlightedColumns"
                            :show-changes="showChanges"
                            :show-raw-values="showRawValues"
                            :validation-errors="validationErrors">
                            <template
                              v-for="(_, slot) in $slots"
                              #[slot]="{ item, value }">
                              <slot
                                :name="slot"
                                v-bind="{ item, value }" />
                            </template>
                          </TableCell>
                          <div
                            v-else
                            class="range">
                            <!-- Display change in the following format: {original} -> {new} -->
                            <div
                              v-if="showChanges && item.hasChangesFor != null && item.hasChangesFor(column)"
                              class="flex-h">
                              <span class="highlighted mr-2">{{ item.getChangeFor(column).from }}</span> →
                              <span class="highlighted ml-2">{{ item.getChangeFor(column).to }}</span>
                            </div>
                            <!-- When rawValues are available (bulk v4), display them instead of the property value -->
                            <template v-if="showRawValues && item.rawValues != null">
                              {{ item.rawValues[column.property] }}
                            </template>
                            <div
                              v-for="(value, n) in componentValues(column, item)"
                              v-else
                              :key="`range-${column.key}-${n}`"
                              :name="column.key + '-' + n"
                              :value="value"
                              :item="item"
                              class="range-fragment">
                              {{ value }}
                            </div>
                          </div>
                          <div class="edit-button-placeholder" />
                          <div class="edit-field-placeholder" />
                          <div class="loader-placeholder" />
                          <div
                            v-if="
                              (highlightedColumns?.length === 0 || highlightedColumns?.includes(column.property)) &&
                              validationErrors?.some(
                                (x) => x.entity === item && Object.keys(x.errors).includes(column.modelProperty),
                              )
                            "
                            v-tooltip="
                              validationErrors
                                ?.find(
                                  (x) =>
                                    x.entity === item &&
                                    Object.keys(x.errors).includes(
                                      column.validationPropertyName ||
                                        column.editablePropertyName ||
                                        column.isProxyForProperty?.editablePropertyName ||
                                        column.property,
                                    ),
                                )
                                ?.errors[column.modelProperty].join(', ')
                            "
                            class="validation-error">
                            <IconInfo />
                          </div>
                          <div
                            v-if="i === 0 && showErrorSummary && validationErrors?.some((x) => x.entity === item)"
                            v-tooltip="
                              `${$t('messages.thisItemWillNotBeSavedAsThereAreOneOrMoreValidationErrors')}
                               <br><ul style='margin-top: 0.6rem; padding-left: 1rem;'>${validationErrors
                                 ?.filter((x) => x.entity === item)
                                 .map((x) => Object.values(x.errors))
                                 .flat()
                                 .map((x) => `<li style='font-size: 0.9em;'>${x}</li>`)
                                 .join('')}</ul>`
                            "
                            class="validation-error">
                            <IconInfo />
                          </div>
                        </div>
                      </div>
                    </td>
                  </template>
                </tr>
                <!-- EXPANDED ROW -->
                <Transition name="squish">
                  <tr
                    v-if="expandedRows.has(lineNumber)"
                    :class="`${getItemType(item) ?? ''} ${getAlternatingStyle(lineNumber)}`">
                    <td
                      class="expanded-row-container"
                      :colspan="orderedColumns.length + 1 + (allowSelection ? 1 : 0)">
                      <slot
                        name="expandedRow"
                        :key="lineNumber"
                        :item="item" />
                    </td>
                  </tr>
                </Transition>
              </template>
            </template>
            <tr
              v-if="summaryRow && itemsInternal.length > 0 && !isLoading"
              class="summary-row">
              <td v-if="allowSelection" />
              <template
                v-for="(column, i) in orderedColumns"
                :key="`summary-row-${column.key}`">
                <td class="data-cell">
                  <div
                    class="cell-wrapper"
                    :class="{ money: column.isMoney }"
                    :style="`${column.style}; ${i === 0 ? 'padding-top: 0px; padding-bottom: 0px;' : ''};`">
                    <slot :name="`${column.key}-summary`" />
                  </div>
                </td>
              </template>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        v-if="isLoading && showTable"
        class="loading-row">
        <TableSkeleton :nb-rows="pageSize" />
      </div>
      <div
        v-if="itemsInternal.length === 0 && !isLoading && showTable"
        class="state-row">
        <div
          v-if="!tableFilters?.isFiltered"
          class="no-results"
          colspan="1000">
          <slot name="no-records" />
          <div
            v-if="!hasNoRecordsSlot"
            class="no-records">
            <IconColoredSad />
            {{ capitalize($t('messages.noRecordsFound')) }}
          </div>
        </div>
        <div v-else>
          <slot name="no-match" />
          <div
            v-if="!hasNoRecordsSlot"
            class="no-records-svg-slot">
            <IconColoredSad />
            {{ capitalize($t('messages.noRecordsMatchYourFilters')) }}
          </div>
        </div>
      </div>
      <div
        v-show="!isLoading"
        v-if="!!itemsInternal?.length && showTable && pagination"
        class="table-footer">
        <div class="page-nav-container">
          <Pagination
            v-if="pagination"
            v-model="pageIndex"
            size="md"
            variant="primary"
            :total-rows="totalCount"
            :per-page="pageSize" />
        </div>
        <div
          v-show="totalCount > 10 && pagination"
          class="page-size-container">
          <Multiselect
            v-model="selectedPageSizeOption"
            name="pageSize"
            track-by="name"
            label="name"
            :placeholder="capitalize($t('messages.selectItemsPerPage'))"
            :options="pageSizeOptions"
            :show-labels="false"
            :allow-empty="false" />
        </div>
      </div>
      <input
        type="hidden"
        name="lastDownloadedFilename"
        :value="lastDownloadedFilename" />
    </div>

    <!-- COLUMNS SELECTION MODAL -->
    <TableColumnsConfigModal
      ref="tableColumnsConfigModal"
      :editable-columns="editableColumns"
      @columns-updated="confirmColumnChanges"
      @reset-columns="resetColumns"
      @update:visible="(visibility) => $emit('isColumnModalVisible', visibility)" />

    <!-- DOWNLOAD MODAL -->
    <TableDownloadModal
      ref="tableDownloadModal"
      :client-side-filtering="clientSideFiltering"
      :table-download-columns="tableDownloadColumns"
      :export-file-name="exportFileName"
      @download="(data) => $emit('download', data)" />
  </section>
</template>

<script setup lang="ts" generic="T extends TableEntity">
  import {
    App,
    capitalize,
    ComponentPublicInstance,
    computed,
    createApp,
    onBeforeMount,
    onBeforeUnmount,
    onMounted,
    Ref,
    ref,
    useSlots,
    useTemplateRef,
    watch,
  } from 'vue';
  import { ComponentExposed } from 'vue-component-type-helpers';
  import Multiselect from 'vue-multiselect';

  import GoogleAnalyticsApi from '@/common/apis/GoogleAnalyticsApi';
  import { LoaderSmall } from '@/common/components/controls';
  import ExpandButton from '@/common/components/controls/table/ExpandButton.vue';
  import TableCell from '@/common/components/controls/table/TableCell.vue';
  import TableColumn from '@/common/components/controls/table/TableColumn';
  import TableColumnsConfigModal from '@/common/components/controls/table/TableColumnsConfigModal.vue';
  import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
  import TableDownloadModal from '@/common/components/controls/table/TableDownloadModal.vue';
  import { TableEntity } from '@/common/components/controls/table/TableEntity';
  import TableEvents from '@/common/components/controls/table/TableEvents.enum';
  import { ITableFilter } from '@/common/components/controls/table/TableFilter';
  import TableFilters from '@/common/components/controls/table/TableFilters.vue';
  import TableProps from '@/common/components/controls/table/TableProps';
  import TableSkeleton from '@/common/components/controls/table/TableSkeleton.vue';
  import { TableSlots } from '@/common/components/controls/table/TableSlots';
  import TableSettings from '@/common/components/controls/table/TableUserSettings/TableSettings';
  import ButtonGroup from '@/common/components/ui/ButtonGroup.vue';
  import AnimatedThumb from '@/common/icons/animated/AnimatedThumb.vue';
  import { IconColoredSad } from '@/common/icons/colored';
  import { IconColumns, IconDownload, IconInfo, IconSortAsc, IconSortDesc } from '@/common/icons/monochrome';
  import { Filter, FilterGroup, FilterLogicalOperator, QueryParams } from '@/common/models';
  import Exception from '@/common/models/Exception';
  import OrganizationType from '@/common/models/memberships/OrganizationType.enum';
  import UUIDFilter from '@/common/models/query-params/UUIDFilter';
  import GlobalEventBus from '@/GlobalEventBus';
  import GlobalEvents from '@/GlobalEvents';
  import { isObjectEmpty } from '@/globals';
  import Pagination from '@/org-app/components/controls/Pagination.vue';
  import { mapGetters, mapOrgAppGetters } from '@/utils/StateMapping';
  import ThrottledTaskRunner from '@/utils/ThrottledTaskRunner';
  import i18n from '@/vueI18n';

  import EditCellButton from './EditCellButton.vue';
  import Selection from './Selection.vue';
  import TableCellEditor from './TableCellEditor.vue';

  const slots = useSlots();

  type ColumnsKeys = keyof TableProps<T>['columns'];

  // Provides proper typing for the slots
  defineSlots<TableSlots<T, ColumnsKeys>>();

  const {
    id,
    category,
    allowRowExpansion,
    items = new Array<T>(),
    columns = {} as Record<ColumnsKeys, TableColumn>,
    pagination,
    clientSidePagination,
    allowSelection,
    allowSelectionAll = true,
    canSelectItem,
    lockSelection,
    resetSelectionOnUpdate = true,
    textSearchProperties,
    showFilters,
    fixedFilters,
    totalCount = 0,
    sortColumn,
    sortAscending = true,
    textSearch,
    isLoading = true,
    expand = [],
    forceFixedWidth,
    firstColumnSize = 'lg',
    firstColumnFixed,
    dataRowHeight = '4rem',
    dataRowLineHeight,
    enableSearchSection = true,
    enableDownload,
    tableDownloadColumns = [],
    summaryRow,
    castShadow = true,
    enableColumnReorder = true,
    allowComparison,
    enableClientSideSorting,
    clientSideFiltering,
    exportFileName = '',
    isMainTable,
    shouldDrawColumns,
    isEditable,
    showTable = true,
    showHeaders = true,
    showSaveBtn = true,
    noPadding,
    highlightedColumns = [],
    validationErrors = [],
    showChanges,
    showRawValues,
    showErrorSummary,
    getAction,
    maximumBulkUpdateRecordCount = QueryParams.MAXIMUM_BULK_UPDATE_RECORD_COUNT,
    trackBy = 'id',
    rowFullHeight,
    useFiltersDropDown = true,
    selectionTooltipMessage,
    assignInlineEditButtonStyles = true,
    enableViews = false,
    enableOnlySearchSection = false,
    pageSizeOptionsAvailable = [],
    buttonActionOptions = null,
  } = defineProps<TableProps<T>>();

  interface IEmits {
    (e: 'download'): void;
    (e: 'filtersChanged', filters): void;
    (e: 'clientSideFiltersChanged', filters): void;
    (e: 'filtersStringChanged', value: string);
    (e: 'dateRangesChanged', value: Filter[]);
    (e: 'textSearchTermChanged', value: string);
    (e: 'pageIndexChanged', pageIndex: number);
    (e: 'pageSizeChanged', pageSize: number);
    (e: 'rowClick', item);
    (e: TableEvents.SELECTION_CHANGED, selectedItems: T[]);
    (e: TableEvents.LOADING_SELECTION);
    (e: 'loaded');
    (e: 'bulkCreationChangesSaved');
    (e: 'isColumnModalVisible', visibility: boolean);
    (e: TableEvents.COMMIT_FIELD_EDITING, payload);
    (e: 'toggleComparison', value: boolean);
    (e: 'onButtonActionOptionsChanged', id: string);
  }

  const emits = defineEmits<IEmits>();

  const allItemsInSearchQueryParams: Ref<QueryParams> = ref();
  const canBeEdited: Ref<boolean> = ref();
  const columnsActualWidth: Ref<number[]> = ref([]);
  const columnBeingEdited: Ref<TableColumn> = ref();
  const columnBeingResized: Ref<TableColumn> = ref();
  const columnResizingOriginalWidth: Ref<number> = ref();
  const columnResizingStartX: Ref<number> = ref();
  const columnsInternal: Ref<TableColumn[]> = ref([]);
  const didChooseToSelectAllItemsInSearch: Ref<boolean> = ref(false);
  const editCellButtonComponent: Ref<App<Element>> = ref();
  const editCellButtonInstance: Ref<ComponentPublicInstance> = ref();
  const editableColumns: Ref<TableColumn[]> = ref([]);
  const entityBeingEdited: Ref<T> = ref();
  const isLoadingSelections: Ref<boolean> = ref(false);
  const isSelectAllItemsInSearchLinkVisible: Ref<boolean> = ref(false);
  const isInitialLoad: Ref<boolean> = ref(true);
  const lastDownloadedFilename: Ref<string> = ref();
  const orderedColumns: Ref<TableColumn[]> = ref([]);
  const pageIndex: Ref<number> = ref(1); // Not zero based
  const pageSize: Ref<number> = ref(10);
  const queryParamsInternal: Ref<QueryParams> = ref(QueryParams.all());
  const sortAscendingInternal: Ref<boolean> = ref(true);
  const sortColumnInternal: Ref<TableColumn> = ref();
  const selectedItems: Ref<T[]> = ref([]);
  const expandedRows: Ref<Set<number>> = ref(new Set<number>());

  const tableSettings: Ref<TableSettings> = ref();
  const isSorting: Ref<boolean> = ref(false);
  const scrollLeftBeforeSorting: Ref<number> = ref(0);

  const tableFilters = useTemplateRef<ComponentExposed<typeof TableFilters>>('tableFilters');
  const tableScroller = useTemplateRef('tableScroller');
  const headers = useTemplateRef('headers');
  const tableColumnsConfigModal =
    useTemplateRef<ComponentExposed<typeof TableColumnsConfigModal>>('tableColumnsConfigModal');
  const tableDownloadModal = useTemplateRef<ComponentExposed<typeof TableDownloadModal>>('tableDownloadModal');

  const itemsInternal = computed(() => {
    if (!items) {
      return [];
    }

    let result = [...items];

    const totalRow = hasTotalRow(items);
    if (totalRow) {
      result = [...items.filter((x) => getItemType(x) !== 'data-totals')];
    }

    if (!enableClientSideSorting) {
      if (clientSidePagination) {
        result = result.slice(pageSize.value * (pageIndex.value - 1), pageSize.value * pageIndex.value);
        if (totalRow) {
          return [...result, totalRow];
        }
      }
      return result;
    }

    if (sortColumnInternal.value && sortColumnInternal.value.property) {
      const sortedDataRows = result.sort((a, b) => {
        const aValue = a[sortColumnInternal.value.property];
        const bValue = b[sortColumnInternal.value.property];
        if (typeof a[sortColumnInternal.value.property] === 'string') {
          return sortAscendingInternal.value ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
        }
        return sortAscendingInternal.value ? aValue - bValue : bValue - aValue;
      });
      result = [...sortedDataRows];
    }

    if (clientSidePagination) {
      result = result.slice(pageSize.value * (pageIndex.value - 1), pageSize.value * pageIndex.value);
      if (totalRow) {
        return [...result, totalRow];
      }
    }
    return result;
  });

  const selectedPageSizeOption = computed({
    get() {
      return pageSizeOptions.value.find((x) => x.value === pageSize.value);
    },
    set(value) {
      pageSize.value = value.value;
    },
  });

  const sortDirectionString = computed(() => (sortAscendingInternal.value ? '' : ' desc'));

  const sort = computed(() => {
    if (sortColumnInternal.value == null) {
      return null;
    }

    if (sortColumnInternal.value.orderBy != null) {
      if (typeof sortColumnInternal.value.orderBy === 'function') {
        const sortInfos = sortColumnInternal.value.orderBy(sortColumnInternal.value, sortDirectionString.value);
        const sorts = [];
        sortInfos.forEach((x) => {
          sorts.push(`${x.field}${x.asc == null || x.asc === true ? '' : 'desc'}`);
        });
        return sorts.join();
      }
      if (Array.isArray(sortColumnInternal.value.orderBy)) {
        const sorts = [];
        sortColumnInternal.value.orderBy.forEach((n) => {
          sorts.push(`${n}${sortDirectionString.value}`);
        });
        return sorts.join();
      }

      return `${sortColumnInternal.value.orderBy}${sortDirectionString.value}`;
    }
    if (sortColumnInternal.value.property != null) {
      return `${sortColumnInternal.value.property}${sortDirectionString.value}`;
    }

    return null;
  });

  const pageSizeOptions = computed(() => {
    const pageSizeOptionsInternal = pageSizeOptionsAvailable?.length ? pageSizeOptionsAvailable : [10, 25, 50, 100];
    return pageSizeOptionsInternal.map((x) => {
      return {
        value: x,
        name: `${i18n.global.t('names.itemPerPage', [x])}`,
      };
    });
  });

  const allSelected = computed(() => {
    if (isLoading) {
      return false;
    }
    if (didChooseToSelectAllItemsInSearch.value) {
      return true;
    }
    return itemsInternal.value.every(
      (item) =>
        selectedItems.value.includes(item) || selectedItems.value.find((selectedItem) => selectedItem.id === item.id),
    );
  });

  const { isDemandOrganization, isSupplyOrganization, isCuratorOrganization } = mapOrgAppGetters();

  const orgType = computed<OrganizationType>(() => {
    if (isDemandOrganization.value) {
      return OrganizationType.DEMAND;
    }
    if (isSupplyOrganization.value) {
      return OrganizationType.SUPPLY;
    }
    if (isCuratorOrganization.value) {
      return OrganizationType.CURATOR;
    }
    return OrganizationType.EXCHANGE;
  });

  const hasNoRecordsSlot = computed(() => {
    const noRecordsSlot = slots['no-records'];
    return noRecordsSlot != null;
  });

  const tableScrollerClassNames = computed(() => {
    let classNames = `first-col-${firstColumnSize}`;
    if (firstColumnFixed) {
      classNames = `${classNames} first-col-fixed`;
    }
    if (isLoading) {
      classNames += ' loading';
    }
    if (allowSelection) {
      classNames += ' allow-selection';
    }
    if (allowRowExpansion) {
      classNames += ' allow-row-expansion';
    }
    if (isLoading) {
      classNames += ' loading';
    } else if (itemsInternal.value.length === 0 && !isLoading) {
      classNames += ' no-result';
    }
    return classNames;
  });

  const queryParams = computed(() => {
    return getQueryParams();
  });

  const noResults = computed(() => {
    return !isLoading && itemsInternal.value.length == 0;
  });

  const isAllExpanded = computed(() => {
    return itemsInternal.value.every((_, i) => expandedRows.value.has(i));
  });

  const filterText = computed(() => tableFilters.value?.filterText);
  const availableFilters = computed(() => tableFilters.value?.availableFilters || []);
  const selectedFilters = computed(() => tableFilters.value?.selectedFilters || []);
  const activeFilters = computed(() => tableFilters.value?.activeFilters || []);
  const tableFilterRefs = computed(() => tableFilters.value?.tableFilterRefs);

  watch(
    () => columns,
    () => initColumns(),
    {
      immediate: false,
    },
  );

  watch(pageIndex, (val) => {
    emits('pageIndexChanged', val);
    fireFiltersChanged(false);

    GoogleAnalyticsApi.sendEvent('ChangedPageIndex', { category: category, value: val });
  });

  watch(pageSize, (val) => {
    fireFiltersChanged();
    emits('pageSizeChanged', val);

    GoogleAnalyticsApi.sendEvent('ChangedPageSize', { category: category, value: val });
  });

  watch(
    () => sortColumn,
    (val) => {
      if (sortColumnInternal.value !== val && sortColumnInternal.value?.property !== val?.property) {
        sortColumnInternal.value = val;
        fireFiltersChanged();
      }
    },
  );

  watch(
    () => sortAscending,
    (val) => {
      if (sortAscendingInternal.value !== val) {
        sortAscendingInternal.value = val;
        fireFiltersChanged();
      }
    },
  );

  watch(columnsInternal, () => updateOrderedColumns());

  watch(
    () => isLoading,
    (val) => {
      if (!val) {
        isInitialLoad.value = false;
      }
    },
  );

  watch(
    () => items,
    () => {
      if (isSorting.value) {
        isSorting.value = false;
        setTimeout(() => {
          tableScroller.value.scrollLeft = scrollLeftBeforeSorting.value;
        }, 50);
      }
      checkWritability();
    },
  );

  watch(allSelected, (val) => {
    isSelectAllItemsInSearchLinkVisible.value = val && totalCount > pageSize.value;
  });

  watch(itemsInternal, (val) => {
    expandedRows.value.clear();
    // resetSelectionOnUpdate is the default behaviour, and normally we reset the selected items on data changes
    if (!resetSelectionOnUpdate) {
      return;
    }
    if (selectedItems.value.length > 0) {
      selectedItems.value = [];
    }

    // Go to previous page when all items are removed in the current page index
    if (clientSidePagination && val?.length) {
      const currentRows = [...val.filter((x) => getItemType(x) !== 'data-totals')];
      if (currentRows.length === 0 && pageIndex.value > 1) {
        pageIndex.value = pageIndex.value - 1;
      }
    }
  });

  watch(selectedItems, () => {
    emits(TableEvents.SELECTION_CHANGED, selectedItems.value);
  });

  // #region Throttled functions

  const fireFiltersChangedThrottledTask = ref<ThrottledTaskRunner<boolean>>();

  function setupThrottles() {
    fireFiltersChangedThrottledTask.value = new ThrottledTaskRunner<boolean>((resetPage: boolean) =>
      fireFiltersChangedInternal(resetPage),
    );
  }

  function destroyThrottles() {
    fireFiltersChangedThrottledTask.value.destroy();
  }

  // #endregion

  onBeforeMount(() => {
    setupThrottles();
    tableSettings.value = new TableSettings(orgType.value, id);
    sortAscendingInternal.value = sortAscending;
    sortColumnInternal.value = sortColumn;
    const sortBySetting = tableSettings.value.sortBy.get();
    if (sortBySetting != null) {
      // Check the column being sorted on actually exist in the list of columns supplied to the table.
      if (Object.values<TableColumn>(columns).some((x) => x.property === sortBySetting.property)) {
        sortColumnInternal.value = sortBySetting;
        const sortAscending = tableSettings.value.sortAscending.get();
        if (sortAscending != null) {
          sortAscendingInternal.value = sortAscending;
        }
      } else {
        tableSettings.value.sortBy.delete();
        tableSettings.value.sortAscending.delete();
      }
    }
    const pageSizeSetting = tableSettings.value.pageSize.get();
    if (pageSizeSetting != null) {
      pageSize.value = pageSizeSetting;
    }
    initColumns();
    emits('pageIndexChanged', pageIndex.value);
    emits('pageSizeChanged', pageSize.value);
    GlobalEventBus.$on(GlobalEvents.languageChanged, () => {
      fireFiltersChanged();
    });
  });

  onMounted(async () => {
    emits('loaded');

    window.addEventListener('wheel', onWindowScroll);

    checkWritability();
  });

  onBeforeUnmount(() => {
    destroyThrottles();
    window.removeEventListener('wheel', onWindowScroll);
  });

  function getItemType(item?: T) {
    return item?.type;
  }

  function getQueryParams() {
    queryParamsInternal.value.top = pageSize.value;
    queryParamsInternal.value.skip = (pageIndex.value - 1) * pageSize.value;
    queryParamsInternal.value.filterCollection = tableFilters.value.getFilters();
    queryParamsInternal.value.sort = sort.value;
    queryParamsInternal.value.expand = expand;
    // The table already handle the loading state so there is no need for the global loader to be displayed.
    queryParamsInternal.value.showLoader = false;
    // BE doesn't handle when UUID is set to empty string, so ignore this before making a request
    const uuidFilter = queryParamsInternal.value.filterCollection?.filters?.find((x) => x instanceof UUIDFilter);
    if (uuidFilter && uuidFilter.value === '') {
      queryParamsInternal.value.filterCollection.filters = queryParamsInternal.value.filterCollection.filters.filter(
        (x) => !(x instanceof UUIDFilter),
      );
    }
    return queryParamsInternal.value;
  }

  function dataRowStyle(item, col) {
    let style = '';
    if (dataRowHeight != null) {
      style += `height: ${dataRowHeight};`;
    }
    if (dataRowLineHeight != null) {
      style += `line-height: ${dataRowHeight};`;
    }
    if (col != null) {
      style += col.style;
    }
    return style;
  }

  function toggleAll() {
    if (allSelected.value) {
      selectedItems.value = [];
    } else {
      selectedItems.value = [...itemsInternal.value];
    }
  }

  function fireFiltersChanged(resetPage = true) {
    fireFiltersChangedThrottledTask.value.run(resetPage);
  }

  function fireFiltersChangedInternal(resetPage = true) {
    // Store the actual width of all columns so that we can keep their size
    // while data is loading. This is required since some columns automatically
    // resize to fit their content.
    // The end result is that column don't move around when paging, sorting, filtering.
    if (headers.value) {
      const columnsElements = Array.from(headers.value.children) as HTMLElement[];
      columnsActualWidth.value = columnsElements.map((x) => x.offsetWidth);
    }

    if (resetPage) {
      pageIndex.value = 1;
    }
    saveTableSettings();
    const queryParams = getQueryParams();
    if (!clientSideFiltering) {
      emits('filtersChanged', queryParams);
      emits('filtersStringChanged', tableFilters.value.getSerializedFiltersString());
    } else {
      emits('clientSideFiltersChanged', getClientSideFilters(queryParams));
    }
  }

  function getClientSideFilters(queryParams: QueryParams) {
    const { filterCollection, sort } = queryParams;
    let { filters } = filterCollection;
    const filtersFunction = {
      and: null,
      or: null,
    };
    const indicesToRemove = [];
    const filtersToAdd = [];

    const removeItemsByIndices = (array, indices) => {
      // Sort indices in descending order to remove from the end of the array first
      indices.sort((a, b) => b - a);

      indices.forEach((index) => {
        if (index >= 0 && index < array.length) {
          array.splice(index, 1);
        }
      });

      return array;
    };

    /**
     * In cases where we want to use the 'In Range' filter for floats, a FilterGroup is created instead of 2 Filters with AND.
     * This following if statement will look for FilterGroups that have Filter objects inside filtering on the same property.
     * IE, if there are 2 Filter objects in a FilterGroup that are filter for Greater Than or Equal and Less Than or Equal on
     * the 'Lift' property, that means that an AND operator should be applied instead of an OR, therefore, we remove that FilterGroup
     * and bring out the Filter objects to the parent so that the correct filtering process is applied.
     */
    // Check for FilterGroups
    if (filters.some((filter) => filter instanceof FilterGroup)) {
      const filterGroups = filters.filter((filter) => filter instanceof FilterGroup);
      if (filterGroups.length === 0) return;
      filterGroups.forEach((group, index) => {
        // Check which filter group are using the same property - Meaning it's a 'In Range' Filter and we need to apply AND to it.
        const [firstFilter] = group.filters as Filter[];
        const firstPropertyName = firstFilter.property;
        if (group.filters.every((filter) => filter instanceof Filter && filter.property === firstPropertyName)) {
          indicesToRemove.push(index);
          filtersToAdd.push(...group.filters);
        }
      });
      // Remove FilterGroups that were 'In Range' FilterGroups
      filters = removeItemsByIndices(filters, indicesToRemove);
    }
    // Add Filters back to the parent.
    filters.push(...filtersToAdd);

    // In cases of searching from the text box, we'll need to use an OR grouping instead of an AND
    // Else just check the logical operator of the FilterCollection
    const [filterGroup] = filters;
    if (filterGroup instanceof FilterGroup) {
      filtersFunction.or = Filter.generateOrFilterFunction(filterGroup.filters);
    }
    const filtersWithoutFilterGroup = filters.filter((filter) => filter.type !== 'FilterGroup');
    if (filterCollection.logicalOperator === FilterLogicalOperator.AND) {
      filtersFunction.and = Filter.generateCombinedFilterFunction(filtersWithoutFilterGroup);
    } else if (filtersFunction.or) {
      filtersFunction.or.push(Filter.generateOrFilterFunction(filtersWithoutFilterGroup));
    } else {
      filtersFunction.or = Filter.generateOrFilterFunction(filtersWithoutFilterGroup);
    }
    // eslint-disable-next-line consistent-return
    return { functions: filtersFunction, sort };
  }

  function saveTableSettings() {
    const { isPublicRoute } = mapGetters();
    if (isPublicRoute.value) return;
    tableSettings.value.pageSize.set(pageSize.value);
    tableSettings.value.sortBy.set(sortColumnInternal.value);
    tableSettings.value.sortAscending.set(sortAscendingInternal.value);
    tableSettings.value.columns.set(columnsInternal.value);
    tableSettings.value.showComparison.set(tableFilters.value.showComparison);
  }

  function sortBy(column, event) {
    // Prevent sorting when resizing columns
    if (columnBeingResized.value != null) {
      return;
    }
    // Don't sort when clicking on the column sizer
    const targetClass = event?.target?.className;
    if (targetClass === 'column-sizer' || targetClass === 'line') {
      return;
    }
    scrollLeftBeforeSorting.value = tableScroller.value.scrollLeft;
    isSorting.value = true;
    if (column.property == null || !column.allowSort) {
      return;
    }
    if (sortColumnInternal.value?.property === column?.property) {
      sortAscendingInternal.value = !sortAscendingInternal.value;
    } else {
      sortColumnInternal.value = column;
      sortAscendingInternal.value = true;
    }

    if (!enableClientSideSorting) {
      fireFiltersChanged();
      const value = `${column.property} ${sortAscendingInternal.value ? 'asc' : 'desc'}`;

      GoogleAnalyticsApi.sendEvent('Sorted', { category: category, value });
    }
  }

  function componentValues(column, item) {
    const values = [];
    for (let n = 0; n < column.fragments.length; n += 1) {
      const component = column.fragments[n];
      if (typeof component === 'function') {
        const val = component(item);
        if (val instanceof Array) {
          values.push(val[n]);
        } else {
          values.push(val);
        }
      } else if (typeof component === 'string') {
        values.push(item[component]);
      } else {
        values.push('N/A');
      }
    }

    return values;
  }

  function canSort(field) {
    return (
      (field.property !== undefined || field.orderBy !== undefined) &&
      (field.allowSort === undefined || field.allowSort === true)
    );
  }

  function showColumnsModal() {
    editableColumns.value = [...columnsInternal.value].sort((a, b) => a.index - b.index);
    tableColumnsConfigModal.value.open();
  }

  function confirmColumnChanges() {
    saveOrderedColumns();
    GoogleAnalyticsApi.sendEvent('ChangedColumns', { category: category });
  }

  function saveOrderedColumns() {
    columnsInternal.value = [...editableColumns.value];
    saveTableSettings();
  }

  function updateOrderedColumns() {
    const visibleColumns = columnsInternal.value.filter((x) => x.isVisible);
    const sortedVisibleColumns = visibleColumns.sort((a, b) => a.index - b.index);
    const allButFirstColumn = sortedVisibleColumns.slice(1);
    orderedColumns.value = [
      ...sortedVisibleColumns.slice(0, 1),
      ...allButFirstColumn.filter((x) => highlightedColumns?.some((c) => c === x.property)),
      ...allButFirstColumn.filter((x) => !highlightedColumns?.some((c) => c === x.property)),
    ];
  }

  function resetColumns() {
    initColumns(true);
    saveOrderedColumns();
    tableColumnsConfigModal.value.close();

    GoogleAnalyticsApi.sendEvent('ResetColumns', { category: category });
  }

  function initColumns(resetColumns = false) {
    const columnSettings = tableSettings.value.columns.get();

    const getColumnSetting = (key) =>
      !forceFixedWidth && columnSettings != null && columnSettings.filter((x) => x.key === key).length > 0
        ? columnSettings.filter((x) => x.key === key)[0]
        : null;

    const getColumnWidth = (col, i) => {
      let width = null;
      if (col.widthInPixels != null) {
        width = parseInt(col.widthInPixels, 10);
      } else if (i === 0) {
        if (firstColumnSize === 'lg') {
          width = 300;
        } else if (firstColumnSize === 'md') {
          width = 200;
        } else if (firstColumnSize === 'sm') {
          width = 100;
        }
      }
      return width;
    };

    columnsInternal.value = Object.keys(columns)
      .filter((x) => columns[x].shouldRenderColumn == null || columns[x].shouldRenderColumn === true)
      .map((x, i) => {
        if (!(columns[x] instanceof TableColumn)) {
          throw new Exception(`Supplied column is not an instance of TableColumn, table id: '${id}', column: '${x}'`);
        }
        const column = columns[x].clone();
        column.key = x;
        column.isVisible =
          resetColumns || getColumnSetting(x) === null ? columns[x].isVisible : getColumnSetting(x).isVisible;
        column.index = resetColumns || getColumnSetting(x) === null ? i : getColumnSetting(x).index;
        column.widthInPixels =
          resetColumns || getColumnSetting(x) === null
            ? getColumnWidth(columns[x], i)
            : getColumnSetting(x).widthInPixels;
        return column;
      });
    editableColumns.value = [...columnsInternal.value];
  }

  function handleColumnSizerMouseDown(e, vm) {
    if (vm.minWidthInPixels != null && vm.minWidthInPixels === vm.maxWidthInPixels) return;

    let parentTH = e.srcElement;
    while (parentTH.nodeName.localeCompare('TH') !== 0) {
      parentTH = parentTH.parentElement;
    }
    columnBeingResized.value = vm;
    columnResizingOriginalWidth.value = parseInt(parentTH.clientWidth, 10);
    columnResizingStartX.value = e.clientX;
  }

  function handleTableMouseMove(e) {
    if (columnBeingResized.value != null) {
      columnBeingResized.value.widthInPixels = Math.min(
        columnBeingResized.value.maxWidthInPixels || 1000,
        Math.max(
          columnBeingResized.value.minWidthInPixels || 75,
          columnResizingOriginalWidth.value + (e.clientX - columnResizingStartX.value),
        ),
      );
    }
  }

  function handleTableMouseUp() {
    if (columnBeingResized.value != null) {
      columnBeingResized.value = null;
      saveTableSettings();
    }
  }

  function setPageIndex(val) {
    pageIndex.value = val;
  }

  function setPageSize(val) {
    pageSize.value = val;
  }

  function getColumnHeaderCellWrapperStyle(column, i) {
    if (isLoading) {
      // Maintain column widths while loading data.
      const actualIndex = i + (allowSelection ? 1 : 0);
      if (columnsActualWidth.value[actualIndex]) {
        return `min-width: ${columnsActualWidth.value[actualIndex]}px`;
      }
      return null;
    }
    return column.widthInPixels != null ? `width: ${column.widthInPixels}px;` : null;
  }

  function onRowClick(item, lineNumber, e) {
    // traverse down the clicked pointer to get target element
    const lastNode = (event) => (!event.lastChild ? event : lastNode(event.lastChild));
    const targetNode = lastNode(e.target);
    if (allowRowExpansion) {
      if (expandedRows.value.has(lineNumber)) {
        expandedRows.value.delete(lineNumber);
      } else {
        expandedRows.value.add(lineNumber);
      }
    }

    if (targetNode.localName === 'input' && targetNode.type !== 'checkbox') {
      targetNode.click();
    } else {
      emits('rowClick', item);
    }
  }

  function isFieldEditable(item, column) {
    return (
      (column.mutation != null || column.action != null || column?.handleInlineEditUpdateOutsideOfTable) &&
      (column.isEditableHandler == null || column.isEditableHandler(item.entity || item)) &&
      column.isInlineEditingAvailable
    );
  }

  function onDataRowCellMouseEnter(e, item, column) {
    if (!isEditable || !canBeEdited.value || !isFieldEditable(item, column)) return;

    const buttonPlaceholder = e.target.querySelector('.edit-button-placeholder');
    const cellWrapper = e.target.querySelector('.cell-wrapper');
    const rect = cellWrapper.parentElement.getBoundingClientRect();
    // https://github.com/vuejs/core/issues/629#issuecomment-700549376
    // const ComponentClass = defineComponent({
    //   extends: EditCellButton,
    // });
    // TODO: check that it still works.
    // editCellButtonInstance = createApp(ComponentClass, {
    //   propsData: {
    //     entity: item,
    //     column,
    //     callback: editField,
    //   },
    // }).mount(cellWrapper);
    editCellButtonComponent.value = createApp(EditCellButton, {
      entity: item,
      column,
      callback: editField,
    });
    editCellButtonInstance.value = editCellButtonComponent.value.mount(buttonPlaceholder);

    const buttonElement = editCellButtonInstance.value.$el;

    if (assignInlineEditButtonStyles) {
      buttonElement.style.position = 'fixed';
      buttonElement.style.top = `${rect.y + rect.height / 2 - 12}px`;
      buttonElement.style.left = `${rect.x + rect.width - 30}px`;
    }

    cellWrapper.appendChild(buttonElement);
  }

  function removeEditCellButton() {
    // const cellWrapper = editCellButtonInstance.$el.parentElement;
    // cellWrapper.removeChild(editCellButtonInstance.$el);
    editCellButtonComponent.value.unmount();
    editCellButtonComponent.value = null;
    editCellButtonInstance.value = null;
  }

  function onDataRowCellMouseLeave() {
    if (!isEditable || editCellButtonInstance.value == null) return;
    removeEditCellButton();
  }

  function onCellDoubleClick(event: MouseEvent, item: T, column: TableColumn) {
    // When double-clicking directly on a link, do not trigger cell editing.
    // There is a possibility that the user clicks on an element embedded in a link element, so check path for a link
    editField({ event, item, column });
  }

  const loaderContainer = ref();
  const fieldEditorComponent = ref();
  const fieldEditorInstance = ref();

  function editField({ event, item, column }: { event: MouseEvent; item: T; column: TableColumn }) {
    if (!isEditable || !canBeEdited.value || !isFieldEditable(item, column)) return;

    let cellWrapper = event.target as HTMLElement;
    while (!cellWrapper.classList.contains('cell-wrapper')) {
      cellWrapper = cellWrapper.parentElement;
    }
    const rect = cellWrapper.parentElement.getBoundingClientRect();
    loaderContainer.value = cellWrapper.querySelector('.loader-placeholder');
    entityBeingEdited.value = item;
    columnBeingEdited.value = column;

    // const ComponentClass = defineComponent({ extends: TableCellEditor });
    fieldEditorComponent.value = createApp(TableCellEditor, {
      entity: entityBeingEdited.value,
      column: columnBeingEdited.value,
      commitCallback: commitFieldEditing,
      cancelCallback: cancelFieldEditing,
      handleInlineEditUpdateOutsideOfTable: column.handleInlineEditUpdateOutsideOfTable,
    });

    const container = document.getElementById('table_cell_editor');

    fieldEditorInstance.value = fieldEditorComponent.value.mount(container);

    //
    // Find the table cell width to calculate the min width of the cell editor.
    //
    let td = event.currentTarget as HTMLElement;
    while (td != null && td.tagName !== 'TD') {
      td = td.parentElement;
    }
    const extraWidth = 40;
    const minWidth = td.clientWidth + extraWidth;

    //
    // Get table bounds.
    //
    let table = td?.parentElement;
    while (table != null && !table.className.includes('table-container')) {
      table = table.parentElement;
    }
    const tableBounds = table.getBoundingClientRect();

    //
    // Set min width and position of the cell editor.
    //
    const editorElement = fieldEditorInstance.value.$el;
    editorElement.style.minWidth = `${minWidth}px`;
    editorElement.style.top = `${rect.y + rect.height / 2 - 22}px`;

    // dynamically adjust the x position of the element based on its content
    const observer = new ResizeObserver(([entry]) => {
      const element = entry.target as HTMLElement;
      const elementRect = element.getBoundingClientRect();
      // We want the element to stay inside the table and screen bounds
      const rightBoundary = Math.min(window.innerWidth, tableBounds.right);
      if (rect.x + elementRect.width > rightBoundary) {
        element.style.left = `${rightBoundary - elementRect.width - extraWidth}px`;
      } else {
        element.style.left = `${rect.x}px`;
      }
    });
    observer.observe(editorElement);
  }

  function commitFieldEditing(payload) {
    const thumb = createApp(AnimatedThumb);
    thumb.mount(loaderContainer.value);
    setTimeout(() => {
      thumb.unmount();
    }, 3000);
    cancelFieldEditing();

    emits(TableEvents.COMMIT_FIELD_EDITING, payload);
  }

  function cancelFieldEditing() {
    fieldEditorComponent.value?.unmount();
    fieldEditorComponent.value = null;
    fieldEditorInstance.value = null;
    entityBeingEdited.value = null;
    columnBeingEdited.value = null;
  }

  function checkWritability() {
    const { permissions } = mapGetters();
    if (canBeEdited.value != null) return;

    if (items?.length > 0 && !isObjectEmpty(columns)) {
      const columnsWithEditableClassNames = Object.values<TableColumn>(columns).filter(
        (x) => x.editableClassName != null,
      );
      if (columnsWithEditableClassNames.length > 0) {
        const entityClass = columnsWithEditableClassNames[0].editableClassName;
        const entityClassForPermissions = `${entityClass[0]}${entityClass.toLowerCase().slice(1)}`;
        canBeEdited.value = permissions[`canWrite${entityClassForPermissions}`] || true;
      }
    }
  }

  function onWindowScroll() {
    if (!editCellButtonInstance.value) return;
    removeEditCellButton();
  }

  function formatNumberAndBoldHtml(x) {
    return `<b>${i18n.global.n(x)}</b>`;
  }

  /**
   * Add or remove the toggled entity from the `selectedItems` array
   * @param item
   * @param selected {boolean}
   */
  function handleItemSelection(item: T, selected: boolean) {
    // If we are in "Select all items in search" mode and we unselect one of the items, we exit the "Select all items in search" mode and reset the selection to only visible items
    if (didChooseToSelectAllItemsInSearch.value && !selected) {
      didChooseToSelectAllItemsInSearch.value = false;
      selectedItems.value = [...itemsInternal.value];
    }
    const newSelectedItems = [...selectedItems.value];
    if (selected) {
      // If we aren't resetting the selection on update, we need to verify by id since the objects are re-instantiated.
      const itemAlreadySelected = resetSelectionOnUpdate
        ? selectedItems.value.includes(item)
        : selectedItems.value.some((x) => x.id === item.id);
      if (itemAlreadySelected) {
        return;
      }
      newSelectedItems.push(item);
    } else {
      const itemIndex = resetSelectionOnUpdate
        ? selectedItems.value.indexOf(item)
        : selectedItems.value.findIndex((x) => x.id === item.id);
      const itemAlreadyUnSelected = itemIndex === -1;
      if (itemAlreadyUnSelected) {
        return;
      }
      newSelectedItems.splice(itemIndex, 1);
    }
    selectedItems.value = newSelectedItems;
  }

  async function selectAllItemsInSearch() {
    didChooseToSelectAllItemsInSearch.value = true;
    emits(TableEvents.LOADING_SELECTION);
    isLoadingSelections.value = true;
    const pageSize = 250;
    const maximumPages = maximumBulkUpdateRecordCount / pageSize;
    const totalPages = Math.ceil(totalCount / pageSize);
    let items = [];
    try {
      for (let i = 0; i < Math.min(maximumPages, totalPages); i += 1) {
        const qp = getQueryParams().clone(false);
        qp.skip = i * pageSize;
        qp.top = pageSize;
        qp.showLoader = false;
        items = [...items, ...(await qp.run(getAction)).items];
      }
      selectedItems.value = items;
      allItemsInSearchQueryParams.value = getQueryParams().clone(true);
    } finally {
      isLoadingSelections.value = false;
    }
  }

  function clearSelection() {
    didChooseToSelectAllItemsInSearch.value = false;
    selectedItems.value = [];
  }

  function getAlternatingStyle(lineNumber: number) {
    if (!allowRowExpansion) return '';

    return (lineNumber + 1) % 2 === 0 ? 'even' : 'odd';
  }

  function validateCanSelectItem(item: T, selectedItems: T[]) {
    if (!canSelectItem) return true;
    return canSelectItem(item, selectedItems);
  }

  function toggleExpandAll() {
    if (isAllExpanded.value) {
      expandedRows.value.clear();
    } else {
      itemsInternal.value.forEach((_, i) => expandedRows.value.add(i));
    }
  }

  function getFilterInstance(filter: ITableFilter) {
    return tableFilters.value?.getFilterInstance(filter);
  }

  function selectFilters(filters: ITableFilter[]) {
    tableFilters.value?.selectFilters(filters);
  }

  function hasTotalRow(newItems) {
    return newItems.find((x) => getItemType(x) === 'data-totals');
  }

  defineExpose({
    queryParams,
    pageIndex,
    pageSize,
    selectedItems,
    filterText,
    availableFilters,
    selectedFilters,
    activeFilters,
    tableFilters: tableFilterRefs,
    clearSelection,
    handleItemSelection,
    fireFiltersChanged,
    getFilterInstance,
    setPageIndex,
    setPageSize,
    selectFilters,
  });
</script>

<style lang="scss" rel="stylesheet/scss">
  @use '@/styles/variables' as *;

  $action-cell-width: 2.7rem;

  #app {
    .main-content {
      .table-container {
        &.no-shadow {
          box-shadow: none;
        }

        &.no-table {
          background: none;
          box-shadow: none;
          padding: 0;

          .filter-row {
            margin-bottom: -1rem;
          }
        }

        &.no-padding {
          padding: 0;
        }

        ::-webkit-scrollbar {
          height: 1.2rem;
        }

        ::-webkit-scrollbar-thumb {
          border-radius: 0.6rem;

          &:hover {
            background: var(--primaryColor);
          }
        }

        ::-webkit-scrollbar-track {
          border-radius: 0.6rem;

          &:hover {
            background: var(--tableHeaderBackgroundColor);
          }
        }

        .table-scroller {
          overflow-x: auto;
          overflow-y: visible;

          .entity-table.table {
            border-collapse: collapse;
            border-spacing: 0 0.1rem;

            &.is-loading {
              margin-bottom: 0;
            }

            .first-col-fixed {
              z-index: 1;
            }

            thead {
              th {
                word-break: keep-all;
                white-space: nowrap;
                height: 3.8rem;
                min-width: 1.6rem;
                padding: 0;

                .column-sizer {
                  width: 0.5rem;
                  height: 3.4rem;
                  float: right;
                  cursor: col-resize;
                  margin-right: -1.2rem;
                  margin-top: -0.8rem;
                  margin-bottom: -0.8rem;
                  display: flex;

                  .line {
                    margin: 0 0.3rem 0 0.1rem;
                    background: rgba(0, 0, 0, 0.1);
                    flex-grow: 1;
                  }
                }

                .cell-wrapper {
                  padding: 1rem 0.75rem;

                  &.right {
                    text-align: right;
                  }

                  .editable-icon {
                    display: inline-block;
                    width: 1.4rem;
                    height: 1.4rem;
                    border-radius: 0.7rem;
                    position: relative;
                    top: 0.3rem;
                    padding-left: 0.2rem;
                    padding-top: 0.2rem;
                    margin-left: 0.3rem;

                    border: solid 0.1rem var(--textMutedColor);

                    svg {
                      width: 0.7rem;
                      height: 0.7rem;
                      position: absolute;

                      stroke: var(--textMutedColor);
                    }
                  }

                  .no-text-transform {
                    text-transform: initial;
                  }
                }

                &.expansion-header {
                  padding: 0.9rem 0.75rem;
                }

                &.selection-header,
                &.expansion-header {
                  position: sticky;
                  width: 3rem;
                  z-index: 1;
                }

                &.data-header.first-column {
                  position: sticky;
                  width: 30rem;
                  z-index: 1;
                }

                &:last-of-type {
                  .column-sizer {
                    display: none;
                  }
                }
              }
            }

            .data-cell {
              position: relative;
            }

            .summary-row,
            .data-row {
              td {
                word-break: keep-all;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                min-width: 1.6rem;
                padding: 0;

                .cell-wrapper {
                  padding: 0.75rem;
                  word-break: keep-all;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  display: flex;
                  flex-direction: row;
                  align-items: center;
                  min-height: 0;

                  span,
                  a {
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;

                    &.highlighted {
                      font-weight: 700;

                      color: var(--info) !important;
                      border: solid 0.1rem var(--info);

                      display: inline-block;
                      padding: 0.1rem 0.8rem;
                      border-radius: 0.8rem;
                    }
                  }

                  a {
                    display: flex;
                    align-items: center;
                  }

                  &.right {
                    justify-content: flex-end;
                  }

                  .validation-error {
                    margin-left: 0.6rem;
                    margin-top: 0.3rem;

                    svg {
                      height: 1.6rem;
                      width: 1.6rem;

                      stroke: var(--error);
                    }
                  }
                }

                &.editable {
                  &:hover {
                    .cell-wrapper {
                      padding-right: 2.4rem;
                    }
                  }
                }
              }

              td.selection-cell,
              td.expansion-cell {
                position: sticky;
                width: 3rem;
                z-index: 1;
              }

              td.data-cell.first-column {
                position: sticky;
                width: 30rem;
                z-index: 1;
              }
            }

            .summary-row {
              border-top: solid 1px rgba(0, 0, 0, 0.15);
              // Change this to td to enable the "dark grey" background color
              th {
                background: var(--tableHeaderBackgroundColor) !important;
                color: var(--tableHeaderForegroundColor) !important;
              }
            }

            &.draw-columns {
              th,
              td {
                border: solid 0.1rem #00000011;

                .column-sizer {
                  .line {
                    background: #00000000;
                  }
                }
              }
            }

            &.editable {
              td {
                &:hover {
                  .edit-button {
                    display: block;
                  }
                }
              }
            }
          }

          // If row selection and expansion are disabled
          &:not(.allow-selection):not(.allow-row-expansion) {
            .data-header.first-column,
            .data-cell.first-column {
              left: 0;
            }
          }

          // If we allow selection but not row expansion, we need to adjust the first column position
          &:is(.allow-selection):not(.allow-row-expansion) {
            .selection-header,
            .selection-cell {
              left: 0;
            }
            .data-header.first-column,
            .data-cell.first-column {
              left: $action-cell-width;
            }
          }

          // If we allow expansion but not row selection
          &:not(.allow-selection):is(.allow-row-expansion) {
            .expansion-header,
            .expansion-cell {
              left: 0;
            }
            .data-header.first-column,
            .data-cell.first-column {
              left: $action-cell-width;
            }
          }

          // Both row selection and expansion are enabled, adjust the first column position
          &:is(.allow-selection):is(.allow-row-expansion) {
            .selection-cell,
            .selection-header {
              left: 0;
            }
            .expansion-cell,
            .expansion-header {
              left: $action-cell-width;
            }
            .data-header.first-column,
            .data-cell.first-column {
              left: calc($action-cell-width * 2);
            }
          }

          &.first-col-fixed {
            .entity-table.table {
              thead {
                th.data-header.first-column {
                  max-width: 30rem;
                }
              }

              .data-row {
                td.data-cell.first-column {
                  max-width: 20rem;
                }
              }
            }
          }

          &.no-result {
            overflow-x: auto;
          }

          &.loading {
            overflow-x: hidden;
          }

          &.no-result,
          &.loading {
            margin-left: 0;

            thead tr th:nth-of-type(2) {
              margin-left: 0;
              position: absolute;
            }
          }

          &.first-col-xs {
            &.no-result,
            &.loading {
              margin-left: 0;

              thead tr th:nth-of-type(2) {
                margin-left: 0;
                position: initial;
              }
            }

            .entity-table.table {
              thead {
                th:nth-of-type(1) {
                  margin-left: -7rem;
                }

                th:nth-of-type(2) {
                  margin-left: -6rem;
                  width: 6rem;
                }
              }

              .data-row {
                td:nth-of-type(1) {
                  margin-left: -7rem;
                }

                td:nth-of-type(2) {
                  margin-left: -6rem;
                  max-width: 6rem;
                  width: 6rem;
                }
              }
            }

            &.first-col-fixed {
              .entity-table.table {
                thead {
                  th:nth-of-type(2) {
                    max-width: 6rem;
                  }
                }

                .data-row {
                  td:nth-of-type(2) {
                    max-width: 6rem;
                  }
                }
              }
            }
          }

          &.first-col-sm {
            &.no-result,
            &.loading {
              margin-left: 0;

              thead tr th:nth-of-type(2) {
                margin-left: 0;
                position: initial;
              }
            }

            .entity-table.table {
              thead {
                th:nth-of-type(1) {
                  margin-left: -13rem;
                }

                th:nth-of-type(2) {
                  margin-left: -12rem;
                  width: 12rem;
                }
              }

              .data-row {
                td:nth-of-type(1) {
                  margin-left: -13rem;
                }

                td:nth-of-type(2) {
                  margin-left: -12rem;
                  max-width: 12rem;
                  width: 12rem;
                }
              }
            }

            &.first-col-fixed {
              .entity-table.table {
                thead {
                  th:nth-of-type(2) {
                    max-width: 12rem;
                  }
                }

                .data-row {
                  td:nth-of-type(2) {
                    max-width: 12rem;
                  }
                }
              }
            }
          }

          &.first-col-md {
            &.no-result,
            &.loading {
              margin-left: 0;

              thead tr th:nth-of-type(2) {
                margin-left: 0;
                position: absolute;
              }
            }

            thead {
              th:nth-of-type(1) {
                position: absolute;
                margin-left: -23rem;
              }

              th:nth-of-type(2) {
                margin-left: -20rem;
                width: 20rem;
              }
            }

            .data-row {
              td:nth-of-type(1) {
                margin-left: -23rem;
              }

              td:nth-of-type(2) {
                margin-left: -20rem;
                width: 20rem;
              }
            }

            &.first-col-fixed {
              .entity-table.table {
                thead {
                  th:nth-of-type(2) {
                    max-width: 20rem;
                  }
                }

                .data-row {
                  td:nth-of-type(2) {
                    max-width: 20rem;
                  }
                }
              }
            }
          }

          &:not(.allow-selection) {
            .entity-table.table {
              thead {
                th.data-header.first-column {
                  margin-left: -30rem;
                  width: 30rem;
                }
              }

              .data-row {
                td.data-cell.first-column {
                  margin-left: -30rem;
                  width: 30rem;
                }
              }
            }

            &.first-col-xs {
              .entity-table.table {
                thead {
                  th:nth-of-type(1) {
                    margin-left: -6rem;
                    width: 6rem;
                  }
                }

                .data-row {
                  td:nth-of-type(1) {
                    margin-left: -6rem;
                    max-width: 6rem;
                    width: 6rem;
                  }
                }
              }
            }

            &.first-col-sm {
              .entity-table.table {
                thead {
                  th:nth-of-type(1) {
                    margin-left: -12rem;
                    width: 12rem;
                  }
                }

                .data-row {
                  td:nth-of-type(1) {
                    margin-left: -12rem;
                    max-width: 12rem;
                    width: 12rem;
                  }
                }
              }
            }

            &.first-col-md {
              .entity-table.table {
                thead {
                  th:nth-of-type(1) {
                    margin-left: -20rem;
                    width: 20rem;
                  }
                }

                .data-row {
                  td:nth-of-type(1) {
                    margin-left: -20rem;
                    max-width: 20rem;
                    width: 20rem;
                  }
                }
              }
            }
          }

          .expandable-table {
            tbody {
              > tr.data-row:hover {
                td,
                th {
                  &:not(.selectable) {
                    cursor: pointer;
                  }
                }
              }
            }

            .expanded-row-container {
              cursor: unset;
            }
          }
        }

        .filter-row {
          margin-bottom: 1rem;

          .table-actions {
            display: flex;
            align-items: center;
            justify-content: flex-start;
            margin-left: auto;

            > * {
              margin-left: 1rem;

              &:first-child {
                margin-left: 0;
              }
            }
          }

          .table-filters {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            > * {
              margin-right: 1rem;
            }
            .table-actions {
              margin-right: 0rem;
            }

            .spacer {
              flex-grow: 1;
            }

            .page-size-container {
              margin-left: 0.8rem;
            }

            .input-container {
              position: relative;

              input {
                margin-right: 0;
                padding-right: 2.6rem;
              }

              .table-text-filter {
                border-top-left-radius: 0;
                border-bottom-left-radius: 0;
              }

              .delete {
                display: flex;
                align-items: center;
                position: absolute;
                right: 0.1rem;
                top: 0.9rem;
                margin-right: 0.6rem;

                div {
                  display: flex;
                  cursor: pointer;
                }

                svg {
                  fill: #000;
                  stroke: #000;
                  opacity: 0.2;
                  width: 1.6rem !important;
                  height: 1.6rem !important;
                }
              }
            }

            .is-filter-on {
              box-shadow: 0 0 0.5rem var(--successFaded75);
              border-color: var(--success);

              input {
                border-color: var(--success);
              }

              .icon {
                background: var(--success);
                border-color: var(--success);

                svg {
                  stroke: var(--successReverse);
                  fill: var(--successReverse);
                }
              }
            }
          }
        }

        .range {
          display: flex;

          .range-fragment {
            flex-basis: 33.3%;
            text-align: center;

            &:first-child {
              text-align: left;
            }

            &:last-child {
              text-align: right;
            }
          }
        }

        .multiselect {
          min-height: inherit;
          font-size: 1.3rem;

          &.has-selected-options {
            .multiselect__input,
            .multiselect__single {
              width: 0;
            }
          }
        }

        .multiselect__tags {
          min-height: initial;
          border-radius: 0;

          input {
            position: relative !important;
          }

          .multiselect__tag {
            margin-bottom: 0;
          }
        }

        .multiselect__content {
          width: auto;
        }

        .multiselect .multiselect__tags .multiselect__tag {
          margin-bottom: 0.5rem;
          font-size: 1.3rem;
        }

        .multiselect__input,
        .multiselect__single {
          font-size: 1.3rem;
          width: auto;
        }

        .multiselect__select {
          height: 3.5rem;
          /*position: relative;*/
          &::before {
            top: 60%;
          }
        }

        .apply-table-filter {
          margin: 0.8rem 0.8rem 0.8rem 0;
        }

        .clear-table-filter {
          margin: 0.8rem 0.8rem 0.8rem 0;
        }

        @media (min-width: 576px) {
          .apply-table-filter {
            margin-top: 0;
            margin-bottom: 0;
          }
          .clear-table-filter {
            margin-top: 0;
            margin-bottom: 0;
          }
        }

        .table-filter {
          display: flex;
          // margin: 0.8rem 0.8rem 0.8rem 0;

          .input-container {
            margin-right: 1rem;
          }

          .daterangepicker-input[readonly] {
            background-color: #fff !important;
            opacity: 1;
          }

          .daterangepicker-input.empty {
            color: var(--textMutedColor);
          }

          input.form-control {
            margin-right: 0;
          }
        }

        .table-footer {
          display: flex;
          flex-direction: row;
          margin-top: 1rem;

          div {
            flex-basis: 50%;
          }

          .page-nav-container {
            display: flex;
          }
        }

        .page-size-container {
          display: flex;
          flex-direction: row-reverse;

          .multiselect {
            flex-basis: auto;
            max-width: 19rem;
          }
        }

        .no-records {
          display: flex;
          align-items: center;

          svg {
            height: 2.4rem;
            width: 2.4rem;
            margin-right: 0.8rem;
          }
        }

        .state-row {
          .no-records-svg-slot {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: column;

            svg {
              height: 2.4rem;
              width: 2.4rem;
              margin-right: 0.8rem;
            }
          }
        }
      }

      .file-types {
        display: flex;
        justify-content: center;

        .file-type {
          display: flex;
          flex-direction: column;
          align-content: center;
          margin: 0 2rem;

          a {
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 2.4rem;
            border: solid 1px #0074ff;
            border-radius: 6px;
            background-color: #0074ff05;

            &:hover {
              background-color: #fff;
            }
          }

          svg {
            stroke: var(--secondaryColor);
            fill: var(--secondaryColor);

            width: 6.4rem;
            height: 6.4rem;
            margin-right: 0.1rem;
            margin-bottom: 0.6rem;
          }

          &:first-of-type {
            svg {
              margin-left: 0.1rem;
              margin-right: 0;
            }
          }
        }
      }
    }
  }
</style>
